import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Button,
    Typography,
    TextField,
    Alert,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Avatar,
    Box,
    Chip,
    CircularProgress,
    Skeleton,
    Snackbar
} from '@mui/material';
import {
    ArrowBack,
    Save,
    Group as GroupIcon
} from '@mui/icons-material';
import { User } from '../../../Models/User';
import { CreateGroupRequest } from '../../../interface/Group.interface';
import useCurrentUser from '../../../hooks/useCurrentUser';
import useAxios from '../../../hooks/useAxios';
import { ADMIN_VIEWS, NEW_GROUP_URL, USER_LIST_URL, GROUP_NAME_MAX_LENGTH, GROUP_DESCRIPTION_MAX_LENGTH } from '../../../utils/constant';
import { validateGroupForm } from '../../../utils/groupValidation';
import '../../../styles/NewGroupForm.css';
import logger from "../../../utils/logger";

interface NewGroupFormProps {
    selectedMenu: (view: number) => void;
}

const NewGroupForm: React.FC<NewGroupFormProps> = ({ selectedMenu }) => {
    const { currentUser } = useCurrentUser();
    const { getWithAxios, postWithAxios } = useAxios();

    // État du formulaire
    const [groupName, setGroupName] = useState('');
    const [groupDescription, setGroupDescription] = useState('');
    const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // État des données
    const [availableUsers, setAvailableUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);

    // État UI
    const [searchQuery, setSearchQuery] = useState('');
    const [formError, setFormError] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    // Charger les utilisateurs disponibles
    useEffect(() => {
        const loadUsers = async () => {
            if (!currentUser?.tokens?.accessToken) return;

            try {
                const users = await getWithAxios(USER_LIST_URL);
                const data = Array.isArray(users.data) ? users.data : [];
                setAvailableUsers(data);
                setFilteredUsers(data);
            } catch (err) {
                logger.error('Erreur lors du chargement des utilisateurs:', err);
                setSnackbarMessage('Erreur lors du chargement des utilisateurs');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            } finally {
                setIsLoadingUsers(false);
            }
        };

        loadUsers();
    }, [currentUser]);

    // Filtrer les utilisateurs
    useEffect(() => {
        if (!searchQuery) {
            setFilteredUsers(availableUsers);
        } else {
            setFilteredUsers(
                availableUsers.filter(u =>
                    `${u.firstname} ${u.lastname} ${u.email}`
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase())
                )
            );
        }
    }, [searchQuery, availableUsers]);

    // Gestion de la sélection des membres
    const handleMemberToggle = (userUuid: string) => {
        setSelectedMembers(prev =>
            prev.includes(userUuid)
                ? prev.filter(id => id !== userUuid)
                : [...prev, userUuid]
        );
    };

    // Validation & soumission
    const handleSubmit = async () => {
        // Utiliser la validation centralisée
        const validationError = validateGroupForm({
            name: groupName,
            description: groupDescription,
            memberUuids: selectedMembers
        });

        if (validationError) {
            setFormError(validationError);
            return;
        }

        setIsSubmitting(true);
        setFormError('');

        try {
            const groupData: CreateGroupRequest = {
                name: groupName.trim(),
                description: groupDescription.trim() || undefined,
                memberUuids: selectedMembers
            };

            await postWithAxios(NEW_GROUP_URL, groupData);

            setSnackbarMessage('Groupe créé avec succès 🎉');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setTimeout(() => selectedMenu(ADMIN_VIEWS.GROUP_LIST_VIEW), 1500);
        } catch (err) {
            logger.error('Erreur lors de la création du groupe:', err);
            setSnackbarMessage('Erreur lors de la création du groupe');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="new-group-container">
            {/* Header */}
            <div className="new-group-header">
                <div className="header-left">
                    <div className="section-icon">
                        <GroupIcon />
                    </div>
                    <Typography variant="h4" className="section-title">
                        Nouveau Groupe
                    </Typography>
                </div>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => selectedMenu(ADMIN_VIEWS.GROUP_LIST_VIEW)}
                    className="back-button"
                >
                    Retour
                </Button>
            </div>

            {/* Informations du groupe */}
            <Card className="group-info-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Informations du groupe
                    </Typography>
                    <TextField
                        label="Nom du groupe"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        error={!!formError && !groupName.trim()}
                        helperText={formError && !groupName.trim() ? formError : `${groupName.length}/${GROUP_NAME_MAX_LENGTH} caractères`}
                        fullWidth
                        margin="normal"
                        required
                        inputProps={{ maxLength: GROUP_NAME_MAX_LENGTH }}
                    />
                    <TextField
                        label="Description (optionnel)"
                        value={groupDescription}
                        onChange={(e) => setGroupDescription(e.target.value)}
                        multiline
                        rows={3}
                        fullWidth
                        margin="normal"
                        placeholder="Décrivez l'objectif de ce groupe..."
                        helperText={`${groupDescription.length}/${GROUP_DESCRIPTION_MAX_LENGTH} caractères`}
                        inputProps={{ maxLength: GROUP_DESCRIPTION_MAX_LENGTH }}
                    />
                </CardContent>
            </Card>

            {/* Sélection des membres */}
            <Card className="members-selection-card">
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Sélectionner les membres
                    </Typography>

                    <TextField
                        label="Rechercher un membre"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    {selectedMembers.length > 0 && (
                        <Box className="selected-members-preview" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2">
                                {selectedMembers.length} membre(s) sélectionné(s)
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                {selectedMembers.map(memberUuid => {
                                    const user = availableUsers.find(u => u.uuid === memberUuid);
                                    return user ? (
                                        <Chip
                                            key={memberUuid}
                                            label={`${user.firstname} ${user.lastname}`}
                                            onDelete={() => handleMemberToggle(memberUuid)}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ) : null;
                                })}
                            </Box>
                        </Box>
                    )}

                    {isLoadingUsers ? (
                        <Box>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1 }} />
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            <List>
                                {filteredUsers.map((user) => (
                                    <ListItem key={user.uuid} dense>
                                        <ListItemIcon>
                                            <Checkbox
                                                checked={selectedMembers.includes(user.uuid)}
                                                onChange={() => handleMemberToggle(user.uuid)}
                                            />
                                        </ListItemIcon>
                                        <Avatar sx={{ mr: 2 }}>
                                            {user.firstname?.charAt(0)}{user.lastname?.charAt(0)}
                                        </Avatar>
                                        <ListItemText
                                            primary={`${user.firstname} ${user.lastname}`}
                                            secondary={user.email}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {formError && selectedMembers.length === 0 && (
                        <Typography color="error" variant="caption">
                            {formError}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* Actions */}
            <div className="form-actions">
                <Button
                    variant="outlined"
                    onClick={() => selectedMenu(ADMIN_VIEWS.GROUP_LIST_VIEW)}
                    disabled={isSubmitting}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    startIcon={isSubmitting ? <CircularProgress size={18} /> : <Save />}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Création en cours...' : 'Créer le groupe'}
                </Button>
            </div>

            {/* Snackbar feedback */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    elevation={6}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default NewGroupForm;
